import {
  CREATE,
  DELETE,
  FIND,
  GET_BY_PROJECT_ID,
  UPDATE,
  GET_INVENTORY_STATISTICS,
} from './constants';
import { STATUS } from '../../utils/constants';
import { buildQueryKey } from '../../utils/helpers';
import {
  addToDictionary,
  addToList,
  changeListStatus,
  hasPageSizeChanged,
  resetState,
} from '../helpers';

/**
 *
 * @type {{list: {default: {}, pagination: {total: number, pageNumber: null, pageSize: null}, meta: {currentKey: string}}, dictionary: {}}}
 */
const initialState = {
  dictionary: {},
  list: {
    meta: {
      currentKey: 'default',
    },
    pagination: {
      pageNumber: null,
      pageSize: null,
      total: 0,
    },
    default: {},
  },
  inventoryStatistics: [
    { count: 0, category: 'consumables' },
    { count: 0, category: 'tools' },
  ],
};

export default function inventoryReducer(state = initialState, action) {
  let queryKey;
  let listStatus;
  let error;
  let id;
  let resourceModel;
  let resetToDefault;
  let activeCategory;
  let updatedState;
  let updatedInventoryStatistics;
  let categoryIndex;
  let pager;

  switch (action.type) {
    case GET_BY_PROJECT_ID.REQUEST:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;
      resetToDefault = action.payload.resetToDefault;

      if (
        hasPageSizeChanged(state.list.pagination.pageSize, pager?.pageSize) ||
        resetToDefault
      ) {
        updatedState = resetState(state, initialState, {
          queryKey,
          pager,
        });
        return {
          ...state,
          dictionary: updatedState.dictionary,
          list: updatedState.list,
        };
      }

      if (
        [STATUS.RELOADING, STATUS.SUCCESS, STATUS.CACHED].includes(listStatus)
      ) {
        updatedState = changeListStatus(
          state,
          { pager, queryKey },
          STATUS.RELOADING
        );
        return {
          ...state,
          dictionary: updatedState.dictionary,
          list: updatedState.list,
        };
      }
      updatedState = changeListStatus(
        state,
        { pager, queryKey },
        STATUS.LOADING
      );
      return {
        ...state,
        dictionary: updatedState.dictionary,
        list: updatedState.list,
      };
    case GET_BY_PROJECT_ID.SUCCESS:
      resourceModel = action.payload.resourceModel;
      activeCategory = action.payload.query?.filter?.category;
      updatedInventoryStatistics = [...state.inventoryStatistics];
      categoryIndex = updatedInventoryStatistics.findIndex(
        ({ category }) => activeCategory === category
      );
      if (categoryIndex > -1) {
        updatedInventoryStatistics[categoryIndex].count = action.payload.total;
      }
      return {
        ...state,
        dictionary: addToDictionary(
          state.dictionary,
          action.payload.resources,
          resourceModel
        ),
        inventoryStatistics: updatedInventoryStatistics,
        list: addToList(state.list, action.payload),
      };
    case GET_BY_PROJECT_ID.ERROR:
      queryKey = buildQueryKey(action.payload.query);
      pager = action.payload.pager;
      error = action.payload.error;
      listStatus = state.list?.[queryKey]?.[pager?.pageNumber]?.status;

      return changeListStatus(
        state,
        { pager, queryKey, error },
        listStatus === STATUS.RELOADING ? STATUS.CACHED : STATUS.ERROR
      );

    case FIND.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case FIND.SUCCESS:
      const { resource } = action.payload;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [resource.id]: new resourceModel({
            ...state.dictionary[resource.id],
            ...resource,
            findFetched: true,
          }),
        },
      };
    case FIND.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case CREATE.REQUEST:
      return state;
    case CREATE.SUCCESS:
      activeCategory = action.payload.activeCategory;
      updatedInventoryStatistics = [...state.inventoryStatistics];
      categoryIndex = updatedInventoryStatistics.findIndex(
        ({ category }) => activeCategory === category
      );
      if (categoryIndex > -1) {
        updatedInventoryStatistics[categoryIndex].count += 1;
      }
      return {
        ...state,
        inventoryStatistics: updatedInventoryStatistics,
      };
    case CREATE.ERROR:
      return state;

    case UPDATE.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case UPDATE.SUCCESS:
      return state;
    case UPDATE.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };

    case DELETE.REQUEST:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: state.dictionary?.[id]
            ? new resourceModel({ ...state.dictionary[id] }, STATUS.LOADING)
            : new resourceModel({}, STATUS.LOADING),
        },
      };
    case DELETE.SUCCESS:
      id = action.payload.id;

      const newDictionary = { ...state.dictionary };
      delete newDictionary[id];
      activeCategory = action.payload.query?.filter?.category;
      updatedInventoryStatistics = [...state.inventoryStatistics];
      categoryIndex = updatedInventoryStatistics.findIndex(
        ({ category }) => activeCategory === category
      );
      if (categoryIndex > -1) {
        updatedInventoryStatistics[categoryIndex].count -= 1;
      }
      return {
        ...state,
        dictionary: newDictionary,
        inventoryStatistics: updatedInventoryStatistics,
      };
    case DELETE.ERROR:
      id = action.payload.id;
      resourceModel = action.payload.resourceModel;

      return {
        ...state,
        dictionary: {
          ...state.dictionary,
          [id]: new resourceModel(
            { ...state.dictionary[id] },
            STATUS.ERROR,
            action.payload.error
          ),
        },
      };
    case GET_INVENTORY_STATISTICS.SUCCESS:
      return {
        ...state,
        inventoryStatistics: action.payload.inventoryStatistics,
      };

    default:
      return state;
  }
}
